/**
 * Cliente de la API.
 *
 * Una sola funcion (`post`) para WHEN/WHO/WAT porque los tres comparten cuerpo
 * y la misma forma de error. Un `fetch` por endpoint repetiria el parseo del
 * cuerpo de error cinco veces y algun dia divergirian.
 *
 * `apiBase` se resuelve en tiempo de ejecucion: en produccion la web y la API
 * estan detrás del mismo edge, asi que basta con '/api'. En dev, Vite hace de
 * proxy. La variable de entorno es para cuando la web se sirve en un host
 * distinto.
 */

import type { AnalyticsInterest, Answer, DataManagement, Destination, Health, Meta } from './types'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

export class ApiError extends Error {
  readonly status: number
  readonly codes: string[]

  constructor(message: string, status: number, codes: string[] = []) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.codes = codes
  }
}

async function readError(res: Response): Promise<ApiError> {
  let detail = `HTTP ${res.status}`
  let codes: string[] = []
  try {
    const body = await res.json()
    if (typeof body?.detail === 'string') detail = body.detail
    if (Array.isArray(body?.codes)) codes = body.codes.map(String)
  } catch {
    // El cuerpo no es JSON. Se queda el mensaje por defecto en vez de fallar
    // al parsear el error, que taparia el error real con otro error.
  }
  return new ApiError(detail, res.status, codes)
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!res.ok) throw await readError(res)
  return (await res.json()) as T
}

export interface AnswerQuery {
  locale: string
  audience?: string | null
  target?: Record<string, unknown>
  horizon_months?: number
  limit?: number
}

export function askWhen(q: AnswerQuery): Promise<Answer> {
  return request<Answer>('/api/when', {
    method: 'POST',
    body: JSON.stringify({ horizon_months: q.horizon_months ?? 12, ...pick(q) }),
  })
}

export function askWho(q: AnswerQuery): Promise<Answer> {
  return request<Answer>('/api/who', { method: 'POST', body: JSON.stringify(pick(q)) })
}

export function askWhat(q: AnswerQuery): Promise<Answer> {
  return request<Answer>('/api/what', {
    method: 'POST',
    body: JSON.stringify({ limit: q.limit ?? 10, ...pick(q) }),
  })
}

function pick(q: AnswerQuery): Record<string, unknown> {
  const out: Record<string, unknown> = { locale: q.locale }
  if (q.audience) out.audience = q.audience
  if (q.target) out.target = q.target
  return out
}

export const getHealth = () => request<Health>('/health')
export const getMeta = () => request<Meta>('/api/meta')
export const getDataManagement = (locale: string, question?: string) =>
  request<DataManagement>(
    `/api/data/sources?locale=${encodeURIComponent(locale)}` +
      (question ? `&question=${encodeURIComponent(question)}` : ''),
  )

export const getAnalyticsInterest = (locale: string, windowDays = 30) =>
  request<AnalyticsInterest>(
    `/api/analytics/interest?window_days=${windowDays}&locale=${encodeURIComponent(locale)}`,
  )

export const getDestination = () => request<Destination>('/api/analytics/destination')
